import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import AddBuyerAddress from '@components/AddBuyerAddress';
import CustomDropdown from '@components/CustomDropdown';
import { toastError } from '@components/Toast';

import AddBankDetails from '@features/crm/brands/components/AddBankDetails';
import { currentStageColumns } from '@features/crm/utils/helpers';
import TermsAndCondition from '@features/crm/brands/components/terms-and-condition/TermsAndCondition';

import { fetchBrandProfileById } from '@store/actions/BrandProfileAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { fetchAllUserList } from '@store/actions/UserAction';

import { fetchBrands, fetchEnumValues } from '@services/commonService';

import ApiClient from '@lib/apiClient';

import { isCRMAdmin, isCRMManagement } from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

const BrandBasicInfo = () => {
	const tabNames = {
		BASIC_INFO: 'BASIC_INFO',
		ADDRESS: 'ADDRESS',
		BANK_INFO: 'BANK_INFO'
	};

	const [activeTab, setActiveTab] = useState(tabNames.BASIC_INFO);
	const [openModal, setOpenModal] = useState(false);
	const [editableField, setEditableField] = useState(null);
	const [brandProfile, setBrandProfile] = useState({});
	const [selectedLocation, setSelectedLocation] = useState({});
	const [selectedBank, setSelectedBank] = useState({});
	const brandProfileStore = useSelector((store) => store.brandProfileStore);
	const [brands, setBrands] = useState([]);
	const [onBoardingStatus, setOnBoardingStatus] = useState([]);
	const [selectedStages, setSelectedStages] = useState([]);
	const [brandApprovalChannel, setBrandApprovalChannel] = useState([]);
	const [fieldName, setFieldName] = useState(null);
	const [userList, setUserList] = useState([]);
	const userStore = useSelector((store) => store.userStore);
	const { id } = useParams();
	const currentStageRef = useRef();
	const onBoardingRef = useRef();
	const dispatch = useDispatch();

	useEffect(() => {
		if (userStore.length > 0) {
			let users = [];
			userStore.map((user) => users.push({ id: user.id, value: user.name }));
			setUserList(users);
		}
	}, [userStore]);

	const fetchSelectedOnBoardingStatus = async () => {
		try {
			const { data } = await ApiClient.GET(
				`${BASE_URL}/brand/${id}/status-stage/ON_BOARDING`,
				'',
				true
			);
			let tmpStages = [];
			for (let item of data) {
				tmpStages.push(item.stage);
			}
			setSelectedStages(tmpStages);
		} catch {
			toastError('Unable to fetch selected onboard status!');
			dispatch(closeLoader());
		}
	};

	const fetchBrandApprovalChannel = async () => {
		try {
			const { data } = await ApiClient.GET('getBrandApprovalChannel');
			let tempData = data.map((item) => ({ ...item, id: item.id, value: item.name }));
			setBrandApprovalChannel(tempData);
		} catch ({ error }) {
			toastError(error.message);
		}
	};

	useEffect(() => {
		if (brandProfileStore.status === 'ON_BOARDING') {
			fetchEnumValues('info.nitex.app.brand.BrandStatusStage', setOnBoardingStatus);
			fetchSelectedOnBoardingStatus();
		}
	}, [brandProfileStore.status]);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [fieldName]);

	const handleClickOutside = (event) => {
		if (currentStageRef && !currentStageRef.current?.contains(event.target)) {
			if (fieldName === 'currentStage') {
				setFieldName(null);
			}
		}
		if (onBoardingRef && !onBoardingRef.current?.contains(event.target)) {
			if (fieldName === 'onBoarding') {
				setFieldName(null);
			}
		}
	};

	const fetchBrandList = async (search) => {
		dispatch(openLoader());
		let params = `?page=0&size=20`;
		if (search) {
			params += `&name=${search}`;
		}
		await fetchBrands(params).then((res) => {
			let tmpBrands = res.map((item) => ({ ...item, id: item.id, value: item.name }));
			setBrands(tmpBrands);
		});
		dispatch(closeLoader());
	};

	useEffect(() => {
		fetchBrandList();
		fetchBrandApprovalChannel();
		dispatch(fetchAllUserList('', true));
	}, []);

	useEffect(() => {
		setBrandProfile(brandProfileStore);
	}, [brandProfileStore]);

	const onUserSearch = (value) => dispatch(fetchAllUserList(value, true));

	const renderAddressList = () => {
		return brandProfile?.locationResponseList?.map((item) => {
			return (
				<div className="mb-4 position-relative hoveable-edit" key={`brand_location_${item.id}`}>
					<p className="regular-14 gray_dark_02 mb-1">
						{item.title}
						<img
							onClick={() => {
								setSelectedLocation(item);
								setOpenModal(true);
							}}
							src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
							alt="edit"
							className="edit-hover ml-1"
						/>
					</p>
					<p className="regular-14 gray_dark_01">{item.address}</p>
				</div>
			);
		});
	};

	const onCloseModal = (isModified) => {
		if (isModified) {
			dispatch(fetchBrandProfileById(id));
		}
		setSelectedLocation({});
		setSelectedBank({});
		setOpenModal(false);
	};

	const renderBankList = () => {
		return brandProfile?.bankDetailsResponseList?.map((item) => {
			return (
				<div className="w-50 pr-5 position-relative hoveable-edit" key={`bank_${item.id}`}>
					<h2 className="regular-16 gray_dark_01 weight-500 mb-12">
						{item.bankName}
						<img
							onClick={() => {
								setSelectedBank(item);
								setOpenModal(true);
							}}
							src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
							alt="edit"
							className="edit-hover ml-1"
						/>
					</h2>
					<div>
						<p className="regular-14 gray_dark_02 mb-1">
							A/C holder name: <span className="gray_dark_01 ml-1">{item.name}</span>
						</p>
						<p className="regular-14 gray_dark_02 mb-1">
							CD A/C NO: <span className="gray_dark_01 ml-1">{item.accountNumber}</span>
						</p>
						<p className="regular-14 gray_dark_02 mb-1">
							SWIFT CODE: <span className="gray_dark_01 ml-1">{item.swiftCode}</span>
						</p>
						<p className="regular-14 gray_dark_02 mb-1">
							Address: <span className="gray_dark_01 ml-1">{item.bankDetails}</span>
						</p>
					</div>
				</div>
			);
		});
	};

	const handleEditableFieldName = (fieldName) => {
		setEditableField(fieldName);
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setBrandProfile((prev) => ({
			...prev,
			[name]: value
		}));
	};

	const handlePostAPI = async (postData) => {
		try {
			await ApiClient.PUT('updateBrandInfo', postData);
			dispatch(fetchBrandProfileById(id));
			setEditableField(null);
			dispatch(closeLoader());
		} catch {
			toastError('Update failed!');
			dispatch(closeLoader());
		}
	};

	const handleSave = async (fieldName) => {
		dispatch(openLoader());
		let postData = {
			id,
			[fieldName]: brandProfile[fieldName]
		};
		await handlePostAPI(postData);
	};

	const onBrandTypeClick = async (data, type) => {
		dispatch(openLoader());
		let postData = { id };
		if (type === 'APPROVE_CHANNEL') {
			setBrandProfile((prev) => ({
				...prev,
				approvalChannelId: data.id,
				approvalChannelName: data.value
			}));
			postData['approvalChannelId'] = data.id;
		} else if (type === 'APPROVER_ID') {
			setBrandProfile((prev) => ({
				...prev,
				approverId: data.id,
				approverName: data.value
			}));
			postData['approverId'] = data.id;
		} else {
			setBrandProfile((prev) => ({
				...prev,
				parentBrandId: data.id,
				parentBrandName: data.value
			}));
			postData['parentId'] = data.id;
		}
		await handlePostAPI(postData);
	};

	const getBrandCard = (item) => {
		return `<div class="item-with-logo">
				<img src="${
					item.brandLogoDocResponse?.docUrl ||
					`${process.env.PUBLIC_URL}/images/pro_pic_default.svg`
				}" alt="" />
				<span>${item.name}</span>
			</div>`;
	};

	const handleCurrentStage = async (value) => {
		dispatch(openLoader());
		if (value === 'ON_BOARDING') {
			await fetchEnumValues('info.nitex.app.brand.BrandStatusStage', setOnBoardingStatus);
		}
		await setBrandProfile((prevState) => ({ ...prevState, status: value }));
		await handlePostAPI({
			id,
			status: value
		});
		setFieldName(null);
	};

	const renderStages = () => {
		let tmpCurrentStages = currentStageColumns;
		if (isCRMAdmin()) {
			tmpCurrentStages = [...currentStageColumns, { label: 'Pending', value: 'PENDING' }];
		}
		return tmpCurrentStages?.map((item) => {
			return (
				<div className="custom-chekbox" key={`stage_name_${item.value}`}>
					<div className="form-group cursor-pointer">
						<span onClick={() => handleCurrentStage(item.value)}>{item.label}</span>
					</div>
				</div>
			);
		});
	};

	const toggleSelectedStatus = (value) => {
		if (selectedStages.includes(value)) {
			let tmpStages = selectedStages.filter((item) => item !== value);
			saveSelectedOnBoardingStatus(value, true);
			setSelectedStages(tmpStages);
		} else {
			saveSelectedOnBoardingStatus(value);
			setSelectedStages((prev) => [...prev, value]);
		}
	};

	const renderBoardingStatus = () => {
		return onBoardingStatus?.map((item) => {
			return (
				<div className="custom-chekbox" key={`stage_name_${item.value}`}>
					<div className="form-group">
						<input
							type="checkbox"
							id={`onBoarding_${item.value}`}
							name={`onBoarding_${item.value}`}
							value={item.value}
							checked={selectedStages.includes(item.value)}
							onClick={() => toggleSelectedStatus(item.value)}
						/>
						<label htmlFor={`onBoarding_${item.value}`}>
							<span>{item.label}</span>
						</label>
					</div>
				</div>
			);
		});
	};

	const saveSelectedOnBoardingStatus = async (stage, isRemove = false) => {
		try {
			dispatch(openLoader());
			let postData = {
				status: 'ON_BOARDING',
				stage
			};
			if (isRemove) {
				postData.isRemove = true;
			}
			await ApiClient.POST(`${BASE_URL}/brand/${id}/status-stage/ON_BOARDING`, postData, '', true);
			dispatch(fetchBrandProfileById(id));
			dispatch(closeLoader());
		} catch {
			toastError('Unable to fetch selected onboard status!');
			dispatch(closeLoader());
		}
	};

	const handleFieldName = (value) => {
		if (fieldName === value) {
			setFieldName(null);
		} else {
			setFieldName(value);
		}
	};

	const renderStatus = () => {
		let tmpCurrentStages = currentStageColumns;
		if (isCRMAdmin()) {
			tmpCurrentStages = [...currentStageColumns, { label: 'Pending', value: 'PENDING' }];
		}
		return tmpCurrentStages?.find((item) => item.value === brandProfile.status)?.label;
	};

	const renderTabContents = () => {
		if (activeTab === tabNames.BASIC_INFO) {
			return (
				<div className="basic-information d-flex">
					<div className="w-50 pr-5">
						<div className="mb-12">
							<p className="regular-14 gray_dark_02 hoveable-edit mb-1">
								Email{' '}
								<img
									src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
									alt="edit"
									className="edit-hover ml-1"
									onClick={() => handleEditableFieldName('email')}
								/>
							</p>
							<p className="regular-14 gray_dark_01">
								{editableField !== 'email' && <span>{brandProfile.email}</span>}
								{editableField === 'email' && (
									<input
										type="text"
										autoComplete="off"
										name="email"
										value={brandProfile.email}
										autoFocus={true}
										onChange={handleChange}
										onBlur={() => handleSave('email')}
									/>
								)}
							</p>
						</div>
						<div className="mb-12">
							<p className="regular-14 gray_dark_02 mb-1 hoveable-edit">
								Phone{' '}
								<img
									src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
									alt="edit"
									className="edit-hover ml-1"
									onClick={() => handleEditableFieldName('phone')}
								/>
							</p>
							<p className="regular-14 gray_dark_01">
								{editableField !== 'phone' && <span>{brandProfile.phone}</span>}
								{editableField === 'phone' && (
									<input
										type="text"
										autoComplete="off"
										name="phone"
										value={brandProfile.phone}
										onChange={handleChange}
										autoFocus={true}
										onBlur={() => handleSave('phone')}
									/>
								)}
							</p>
						</div>
						<div className="mb-12">
							<p className="regular-14 gray_dark_02 mb-1 hoveable-edit">
								TaxID{' '}
								<img
									src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
									alt="edit"
									className="edit-hover ml-1"
									onClick={() => handleEditableFieldName('taxId')}
								/>
							</p>
							<p className="regular-14 gray_dark_01">
								{editableField !== 'taxId' && <span>{brandProfile.taxId}</span>}
								{editableField === 'taxId' && (
									<input
										autoFocus={true}
										type="text"
										autoComplete="off"
										name="taxId"
										value={brandProfile.taxId}
										onChange={handleChange}
										onBlur={() => handleSave('taxId')}
									/>
								)}
							</p>

							<div className="mb-12 pt-3">
								<p className="regular-14 gray_dark_02 mb-1">Region</p>
								<div className="transparent-select">
									<CustomDropdown
										// type="addItem"
										title=""
										isShowLabel={true}
										onItemClick={(value) => onBrandTypeClick(value, 'APPROVE_CHANNEL')}
										items={brandApprovalChannel}
										selectedItem={brandProfile?.approvalChannelResponse?.name || ''}
									/>
								</div>
							</div>
						</div>
						{isCRMManagement() && (
							<div className="mb-12">
								<p className="regular-14 gray_dark_02 mb-1 hoveable-edit">
									Power BI URL{' '}
									<img
										src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
										alt="edit"
										className="edit-hover ml-1"
										onClick={() => handleEditableFieldName('powerBiUrl')}
									/>
								</p>
								<p className="regular-14 gray_dark_01">
									{editableField !== 'powerBiUrl' && <span>{brandProfile.powerBiUrl}</span>}
									{editableField === 'powerBiUrl' && (
										<input
											autoFocus={true}
											type="text"
											autoComplete="off"
											name="powerBiUrl"
											value={brandProfile.powerBiUrl}
											onChange={handleChange}
											onBlur={() => handleSave('powerBiUrl')}
										/>
									)}
								</p>
							</div>
						)}
						<TermsAndCondition brandId={id} brandProfile={brandProfile} />
					</div>
					<div className="w-50 pl-5">
						<div className="mb-12" onClick={() => handleEditableFieldName('motherBrand')}>
							<p className="regular-14 gray_dark_02 mb-1">Mother company</p>
							<div className="transparent-select">
								<CustomDropdown
									type="addItem"
									title={''}
									isShowLabel={true}
									onItemSearch={fetchBrandList}
									onItemClick={(value) => onBrandTypeClick(value)}
									items={brands}
									customDesign={(item) => getBrandCard(item)}
									selectedItem={brandProfile.parentBrandName || ''}
								/>
							</div>
						</div>
						<div className="mb-12">
							<p className="regular-14 gray_dark_02 mb-1">Child company</p>
							<p className="regular-14 gray_dark_01">{brandProfile.noOfChild}</p>
						</div>

						<div className="mb-12">
							<p className="regular-14 gray_dark_02 mb-1">Approver name</p>
							<div className="transparent-select">
								<CustomDropdown
									type="addItem"
									title={''}
									isShowLabel={true}
									onItemSearch={onUserSearch}
									onItemClick={(value) => onBrandTypeClick(value, 'APPROVER_ID')}
									items={userList}
									selectedItem={brandProfile?.approver?.name || ''}
								/>
							</div>
						</div>
					</div>
					<div className="w-50 pl-5">
						<div className="mb-12 current-stage">
							<p className="regular-14 gray_dark_02 mb-1">Current stage</p>
							<div className="single-select transparent-select mr-12">
								<div
									className="select-component mb-2 position-relative"
									ref={currentStageRef}
								>
									<div
										className="click-box click mb-1"
										onClick={() => handleFieldName('currentStage')}
									>
										<p className="regular-14 ">{renderStatus()}</p>
										<img
											src={`${process.env.PUBLIC_URL}/images/chevron_down.svg`}
											alt=""
										/>
									</div>
									<div
										className={`dropdown-box ${
											fieldName === 'currentStage' ? '' : 'd-none'
										} position-absolute`}
									>
										{renderStages()}
									</div>
								</div>
							</div>
						</div>

						<div className="mb-12">
							<p className="regular-14 gray_dark_02 hoveable-edit mb-1">
								PO Item Identifier
								<img
									src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
									alt="edit"
									className="edit-hover ml-1"
									onClick={() => handleEditableFieldName('poItemIdentifier')}
								/>
							</p>
							<p className="regular-14 gray_dark_01">
								{editableField !== 'poItemIdentifier' && (
									<span>{brandProfile?.poItemIdentifier}</span>
								)}
								{editableField === 'poItemIdentifier' && (
									<input
										type="text"
										placeholder="Write a unique name"
										autoComplete="off"
										name="poItemIdentifier"
										value={brandProfile?.poItemIdentifier}
										autoFocus={true}
										onChange={handleChange}
										onBlur={() => handleSave('poItemIdentifier')}
									/>
								)}
							</p>
						</div>
						{brandProfile.status === 'ON_BOARDING' && (
							<div className="mb-12 on-boarding">
								<p className="regular-14 gray_dark_02 mb-1">On-boarding Status</p>
								<div className="single-select transparent-select mr-12">
									<div
										className="select-component mb-2 position-relative"
										ref={onBoardingRef}
									>
										<div
											className="click-box click mb-1"
											onClick={() => handleFieldName('onBoarding')}
										>
											<p className="regular-14 ">
												{brandProfile?.currentOnBoardingStage}
											</p>
											<img
												src={`${process.env.PUBLIC_URL}/images/chevron_down.svg`}
												alt=""
											/>
										</div>
										<div
											className={`dropdown-box ${
												fieldName === 'onBoarding' ? '' : 'd-none'
											} position-absolute`}
										>
											{renderBoardingStatus()}
										</div>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			);
		} else if (activeTab === tabNames.ADDRESS) {
			return (
				<div className="basic-information d-flex">
					<div className="w-50 pr-5">{renderAddressList()}</div>
					{openModal && (
						<AddBuyerAddress
							details={{ brandId: id }}
							onAddBuyerAddress={onCloseModal}
							onCloseLocation={onCloseModal}
							editedLocationInfo={selectedLocation}
							isEditLocation={Object.keys(selectedLocation).length > 0}
						/>
					)}
				</div>
			);
		} else if (activeTab === tabNames.BANK_INFO) {
			return (
				<div className="basic-information d-flex">
					{renderBankList()}
					{openModal && (
						<AddBankDetails
							selectedItem={selectedBank}
							isEditable={Object.keys(selectedBank).length > 0}
							brandId={id}
							closeModal={onCloseModal}
						/>
					)}
				</div>
			);
		}
	};

	const onTabChange = (value) => {
		setOpenModal(false);
		setActiveTab(value);
	};

	const renderAddButton = () => {
		if (activeTab === tabNames.BANK_INFO || activeTab === tabNames.ADDRESS) {
			return (
				<div className="add">
					<button className="button text" onClick={() => setOpenModal(true)}>
						<span className="mr-1 gray_dark_01">Add</span>
						<img
							src={`${process.env.PUBLIC_URL}/icons/add-round.svg`}
							alt="add"
							className="ml-1"
						/>
					</button>
				</div>
			);
		}
	};

	return (
		<>
			<div className="tab-contents">
				<div className="d-flex justify-content-between">
					<div className="inner-tabs">
						<ul>
							<li
								onClick={() => onTabChange(tabNames.BASIC_INFO)}
								className={activeTab === tabNames.BASIC_INFO ? 'active' : ''}
							>
								Basic info
							</li>
							<li
								onClick={() => onTabChange(tabNames.ADDRESS)}
								className={activeTab === tabNames.ADDRESS ? 'active' : ''}
							>
								Address
							</li>
							<li
								onClick={() => onTabChange(tabNames.BANK_INFO)}
								className={activeTab === tabNames.BANK_INFO ? 'active' : ''}
							>
								Bank info
							</li>
						</ul>
					</div>
					{renderAddButton()}
				</div>
				{renderTabContents()}
			</div>
		</>
	);
};

export default BrandBasicInfo;
